/**
 * Procedural, monochrome robots. The immutable task ID keeps the same face across builds and views,
 * without image storage or provisioning work. Keep the seed and part order
 * stable: changing either would change existing agents' identities.
 */
function robotParts(identity: string) {
  let seed = 2166136261
  for (let i = 0; i < identity.length; i++) {
    seed = Math.imul(seed ^ identity.charCodeAt(i), 16777619)
  }
  const pick = (count: number) => {
    seed += 0x6d2b79f5
    let value = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value)
    return Math.floor(((value ^ (value >>> 14)) >>> 0) / 4294967296 * count)
  }
  return {
    head: pick(6), antenna: pick(6), ears: pick(5), eyes: pick(7),
    mouth: pick(6), brow: pick(5), cheeks: pick(4), width: pick(3), spacing: pick(3),
  }
}

export function RobotAvatar({ identity, size = 32, variant = 'default' }: { identity: string; size?: number; variant?: 'default' | 'redteam' }) {
  const parts = robotParts(identity)
  const redteam = variant === 'redteam'
  const left = 9 - parts.width
  const right = 48 - left
  const eyeX = 18 - parts.spacing
  const eye = (x: number) => {
    if (redteam) return <g transform={x < 24 ? undefined : 'translate(48 0) scale(-1 1)'}>
      {parts.eyes % 3 === 0
        ? <path d={`M${eyeX - 3} 23 L${eyeX + 3} 25 L${eyeX - 1} 28 Z`} fill="currentColor" stroke="none" />
        : parts.eyes % 3 === 1
          ? <path d={`M${eyeX - 3} 23 L${eyeX + 3} 25 Q${eyeX + 2} 29 ${eyeX - 2} 27 Z`} fill="currentColor" stroke="none" />
          : <path d={`M${eyeX - 3} 24 L${eyeX + 3} 26 M${eyeX - 2} 27 H${eyeX + 1}`} />}
    </g>
    switch (parts.eyes) {
      case 0: return <circle cx={x} cy="25" r="1.8" fill="currentColor" stroke="none" />
      case 1: return <path d={`M${x - 2} 26 Q${x} 22 ${x + 2} 26`} />
      case 2: return <rect x={x - 1.4} y="22.5" width="2.8" height="5" rx="1.4" fill="currentColor" stroke="none" />
      case 3: return <circle cx={x} cy="25" r="2.5" />
      case 4: return x < 24
        ? <circle cx={x} cy="25" r="1.8" fill="currentColor" stroke="none" />
        : <path d={`M${x - 2} 25 H${x + 2}`} />
      case 5: return <rect x={x - 2} y="23" width="4" height="4" rx="1" />
      default: return <path d={`M${x - 2} 24 H${x + 2}`} />
    }
  }
  const heads = [
    <rect key="round" x={left} y="15" width={right - left} height="25" rx="7" />,
    <rect key="square" x={left} y="15" width={right - left} height="25" rx="3" />,
    <rect key="pill" x={left} y="15" width={right - left} height="25" rx="12" />,
    <path key="bevel" d={`M${left + 5} 15 H${right - 5} L${right} 20 V35 L${right - 5} 40 H${left + 5} L${left} 35 V20 Z`} />,
    <path key="dome" d={`M${left} 27 Q${left} 15 24 15 Q${right} 15 ${right} 27 V35 Q${right} 40 ${right - 5} 40 H${left + 5} Q${left} 40 ${left} 35 Z`} />,
    <path key="taper" d={`M${left + 4} 15 H${right - 4} Q${right + 1} 15 ${right} 21 L${right - 2} 35 Q${right - 3} 40 ${right - 7} 40 H${left + 7} Q${left + 3} 40 ${left + 2} 35 L${left} 21 Q${left - 1} 15 ${left + 4} 15 Z`} />,
  ]
  const antennae = [
    <g key="dot"><path d="M24 15 V9" /><circle cx="24" cy="6.5" r="2" /></g>,
    <g key="twin"><path d="M17 15 V10 M31 15 V10" /><circle cx="17" cy="8" r="1.5" /><circle cx="31" cy="8" r="1.5" /></g>,
    <g key="tilt"><path d="M24 15 V11 L29 7" /><circle cx="30" cy="6" r="1.5" /></g>,
    <path key="loop" d="M20 15 V11 A4 4 0 0 1 28 11 V15" />,
    <g key="dish"><path d="M24 15 V10 M20 7 Q24 12 28 7" /><circle cx="24" cy="6" r="1" fill="currentColor" stroke="none" /></g>,
    <path key="fin" d="M21 15 V7 L27 11 V15" />,
  ]
  const mouths = [
    <path key="smile" d="M20 32 Q24 36 28 32" />,
    <path key="small" d="M22 33 H26" />,
    <rect key="grille" x="20" y="31" width="8" height="4" rx="2" />,
    <path key="cat" d="M19 32 Q21.5 36 24 32 Q26.5 36 29 32" />,
    <circle key="oh" cx="24" cy="33" r="2" />,
    <path key="open" d="M20 31 H28 Q28 36 24 36 Q20 36 20 31 Z" />,
  ]
  // Reuse the same selected parts so the darker face retains its generated identity.
  const pointedAntennae = [
    <path key="horns" d="M16 15 Q11 11 13 5 L20 15 M28 15 L35 5 Q37 11 32 15" />,
    <path key="broken" d="M18 15 L15 6 L21 10 M30 15 L33 5 L27 9" />,
    <path key="crooked" d="M16 15 L12 6 L21 15 M27 15 L34 4 L33 15" />,
  ]
  const crookedMouths = [
    <path key="fangs" d="M18 31 Q24 34 30 30 Q28 37 22 36 Z M21 32 L23 35 L24 33 M27 32 V35" />,
    <path key="stitched" d="M18 34 L29 31 M20 32 L21 35 M24 31 L25 34 M28 30 L29 33" />,
    <path key="grin" d="M18 31 L21 33 L24 31 L27 33 L30 30 Q28 37 23 36 Z" />,
  ]

  return <svg className="robot-avatar" width={size} height={size} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {redteam ? pointedAntennae[parts.antenna % pointedAntennae.length] : antennae[parts.antenna]}
    {[false, true].map(mirror => <g key={String(mirror)} transform={mirror ? 'translate(48 0) scale(-1 1)' : undefined}>
      {parts.ears === 0 && <path d={`M${left} 24 H${left - 3} V31 H${left}`} />}
      {parts.ears === 1 && <path d={`M${left} 23 C${left - 6} 23 ${left - 6} 32 ${left} 32`} />}
      {parts.ears === 2 && <path d={`M${left - 3} 25 V30`} />}
      {parts.ears === 3 && <circle cx={left - 2} cy="27" r="2" />}
    </g>)}
    <g fill="var(--surface)">{heads[parts.head]}</g>
    {parts.brow === 1 && <path d="M21 19 H27" opacity=".4" />}
    {parts.brow === 2 && <circle cx="24" cy="19" r="1" fill="currentColor" stroke="none" opacity=".4" />}
    {parts.brow === 3 && <path d="M21 18 V20 M24 18 V20 M27 18 V20" opacity=".4" />}
    {parts.brow === 4 && <path d="M22 19 H23 M26 19 H27" opacity=".4" />}
    {eye(eyeX)}{eye(48 - eyeX)}
    {redteam ? crookedMouths[parts.mouth % crookedMouths.length] : mouths[parts.mouth]}
    {parts.cheeks === 1 && <path d={`M${left + 4} 31 H${left + 5} M${right - 5} 31 H${right - 4}`} opacity=".35" />}
    {parts.cheeks === 2 && <g opacity=".35" fill="currentColor" stroke="none"><circle cx={left + 4} cy="31" r="1" /><circle cx={right - 4} cy="31" r="1" /></g>}
    {parts.cheeks === 3 && <path d={`M${left + 3} 31 L${left + 4} 30 M${right - 4} 31 L${right - 3} 30`} opacity=".35" />}
  </svg>
}
